<template>
  <div class="c-report">
    <v-container fluid>
      <v-row
        class="row-columns-wrap"
        align="center"
        justify="space-between"
      >
        <v-col
          cols="12"
          md="5"
          lg="4"
          class="first-col"
        >
          <v-card>
            <v-card-title>
              Reporting date
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <DatePicker
                v-model="date"
                dense
                :events="notReportingDays"
                @onUpdatePickerDate="datepickerChanges($event)"
                @onDatePickerChange="displayAlertDialog(date)"
              />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="7"
          lg="8"
          class="second-col"
        >
          <TotalReport @updateReports="onTotalReportsUpdate" />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          md="5"
          lg="4"
          class="projects-list"
        >
          <h3>Projects And Tasks</h3>
          <v-card class="mt-4">
            <v-card-title>
              <v-text-field
                v-model="searchTasks"
                label="Search Tasks"
                @input="handleSearch"
                hide-details
                dense
                clearable
                clear-icon="mdi-close-circle-outline"
              ></v-text-field>
            </v-card-title>
          </v-card>
          <v-card class="mt-4">
            <v-row>
              <v-col
                cols="12"
                md="7"
                lg="7"
              >
                <v-select
                  class="mx-3"
                  :items="filteredListOfProjectsAndTasks"
                  item-text="Project_Name"
                  item-value="Project_ID"
                  v-model="listForSelect"
                  multiple
                  clearable
                  @click:clear="selectAllProjects = false"
                >
                  <template v-slot:prepend-item>
                    <div class="px-4 py-2">
                      <v-text-field
                        dense
                        hide-details
                        label="Search project"
                        v-model="searchProject"
                      ></v-text-field>
                    </div>
                    <div class="px-4">
                      <v-checkbox
                        dense
                        v-model="selectAllProjects"
                        @change="selectAll"
                        label="Select all"
                      ></v-checkbox>
                    </div>
                    <v-divider></v-divider>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0">
                      <span>{{ item.Project_Name }}</span>
                    </v-chip>
                    <span
                      v-if="index === 1"
                      class="grey--text caption"
                    >
                      (+{{ listForSelect.length - 1 }} others)
                    </span>
                  </template>
                </v-select>
              </v-col>
              <v-col
                cols="12"
                md="4"
                lg="4"
                class="mt-6"
              >
                <RangeDatePickerMenu
                  v-model="reportFilters.DateRange"
                  showButtons
                  type="month"
                  @onOkClick="updateDates"
                  @onCancelClick="onClearDates"
                  @onClearDates="onClearDates"
                />
              </v-col>
            </v-row>
          </v-card>
          <v-expansion-panels
            class="mt-4"
            multiple
            accordion
            v-model="panel"
          >
            <v-expansion-panel
              v-for="(project, projectKey) in listOfProjectsAndTasks"
              v-show="listForSelect.indexOf(project.Project_ID) > -1 && filteredTasksMethod(project.Tasks, []).length"
              :key="projectKey"
            >
              <v-expansion-panel-header>
                {{ project.Project_Name }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-0">
                <v-treeview
                  ref="tree"
                  :active="activeNodes[project.Project_ID] || []"
                  @update:active="val => selectedTask(val, project.Project_ID)"
                  :items="filteredTasksMethod(project.Tasks, [])"
                  :search="searchTasks"
                  :open="openNodes[project.Project_ID] || []"
                  @update:open="val => $set(openNodes, project.Project_ID, val)"
                  item-key="Task_ID"
                  item-text="Name"
                  item-children="children"
                  activatable
                  return-object
                  dense
                  open-on-click
                >
                  <template v-slot:prepend="{ item, open, active }">
                    <v-icon
                      v-if="item.Is_Folder"
                      color="primary"
                      @click="active ? $event.stopPropagation() : null"
                    >
                      {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                    </v-icon>
                    <v-icon
                      v-else
                      color="success"
                      @click="active ? $event.stopPropagation() : null"
                    >
                      mdi-file
                    </v-icon>
                  </template>
                  <template v-slot:label="{ item, active }">
                    <div @click="active ? $event.stopPropagation() : null">
                      {{ item.Name }}
                    </div>
                  </template>
                  <template v-slot:append="{ item }">
                    <v-icon
                      v-if="item.Can_Create_Tasks"
                      color="success"
                      @click.prevent="$event.stopPropagation(); showCreateNewTaskDialog(item, true)"
                    >
                      mdi-plus-circle
                    </v-icon>
                    <v-icon
                      v-if="item.Can_Edit"
                      color="primary"
                      @click.prevent="showCreateNewTaskDialog(item)"
                    >
                      mdi-pencil-circle
                    </v-icon>
                    <v-icon
                      v-if="item.Can_Edit"
                      color="red darken-2"
                      @click.prevent="showDeleteTaskDialog(item)"
                    >
                      mdi-delete-circle
                    </v-icon>
                  </template>
                </v-treeview>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
        <v-col
          cols="12"
          md="7"
          lg="8"
        >
          <h3>Reporting Form(s)</h3>
          <div
            v-if="showReportForm"
            :key="'forms-list-' + updateReportsKey"
            class="reporting-form-wrapper"
          >
            <v-card
              class="my-4"
              v-for="(report, reportKey) in reports"
              :key="reportKey"
            >
              <v-card-text>
                <ReportingForm
                  :date="date"
                  :report="report"
                  @changed="onFormChanged"
                  @updateReports="getReportsByDateAndTask"
                />
              </v-card-text>
            </v-card>
          </div>
          <div
            v-else
            class="mt-4"
          >
            Please select the task
          </div>
        </v-col>
      </v-row>
    </v-container>

    <ConfirmOrDeniedDialog
      v-if="showAlertDialog"
      v-model="showAlertDialog"
      :mode="confirmOrDeniedDialogModes.confirmation"
      title="You can lose your changes"
      message="If you change the date, you can lose your changes in the report form"
      confirm-btn-name="ok"
      @onOkClick="changeDate"
      @onCancelClick="onCancel"
    />

    <v-dialog
      v-model="newTaskDialog"
      max-width="400"
      @click:outside="closeCreateNewTaskDialog"
      @keydown.esc="closeCreateNewTaskDialog"
    >
      <v-card>
        <div class="d-flex justify-space-between">
          <v-card-title>{{ newTask.Task_ID ? 'Edit Task' : 'Create Task' }}</v-card-title>
          <v-btn
            class="mr-1 mt-1"
            icon
            small
            @click="closeCreateNewTaskDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>
        <v-card-text class="py-2">
          <v-text-field
            v-model="newTask.Estimate"
            label="Estimate"
          ></v-text-field>
          <v-text-field
            v-model="newTask.Name"
            label="Task Name"
          ></v-text-field>
          <v-select
            v-if="newTask.Task_ID"
            v-model="newTask.Status"
            :items="taskStatuses"
            label="Task Status"
          ></v-select>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            small
            color="primary"
            outlined
            @click.prevent="closeCreateNewTaskDialog"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            @click="createNewTask"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmOrDeniedDialog
      v-if="deleteTaskDialog"
      v-model="deleteTaskDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      message="You can't restore this task after delete!"
      @onCloseClick="closeDeleteTaskDialog"
      @onCancelClick="closeDeleteTaskDialog"
      @onDeleteClick="deleteTask"
    />
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'Report',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue'),
    TotalReport: () => import('@/components/reporting/TotalReport.vue'),
    ReportingForm: () => import('@/components/reporting/ReportingForm.vue'),
    RangeDatePickerMenu: () => import('@/components/global/RangeDatePickerMenu.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue')
  },
  data () {
    return {
      selectAllProjects: false,
      searchProject: '',
      newTask: {
        Estimate: 40,
        Name: null
      },
      currentMonth: null,
      currentYear: null,
      reportingDays: [],
      searchTasks: null,
      taskIdToDelete: null,
      taskStatuses: ['Completed', 'Development', 'Hold', 'Open', 'QA'],
      deleteTaskDialog: false,
      activeNodes: {},
      newTaskDialog: false,
      date: new Date().toISOString().substring(0, 10),
      today: new Date().toISOString().substring(0, 10),
      panel: [],
      datepickerSaveValue: undefined,
      datepickerWantValue: undefined,
      showAlertDialog: false,
      formWasChanged: false,
      showReportForm: false,
      activeTask: null,
      reports: [],
      listOfProjectsAndTasks: [],
      changed: false,
      currentTaskId: undefined,
      updateReportsKey: 0,
      reportFilters: {
        Employee_ID: null,
        Projects: [],
        DateRange: []
      },
      datesMenu: false,
      lastDayOfTheMonth: null,
      openNodes: {},
      defaultTaskApplied: false,
      isSelecting: false
    }
  },
  async mounted () {
    this.currentMonth = new Date().toISOString().substring(0, 10).split('-')[1]
    this.currentYear = new Date().toISOString().substring(0, 10).split('-')[0]
    this.getTotalReport()
    await this.getProjectsAndTasks(true)
    this.getReportingDays(this.currentYear, this.currentMonth)

    this.$nextTick(() => {
      this.autoSelectDefaultTask()
    })
  },
  computed: {
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    user () {
      return this.$store.getters.getUser
    },
    settings () {
      return this.$store.getters.getSettings
    },
    filteredListOfProjectsAndTasks () {
      return this.listOfProjectsAndTasks.filter((item) => {
        if (this.searchProject) {
          return item.Project_Name.toLowerCase().includes(this.searchProject.toLowerCase())
        }
        return item
      })
    },
    listForSelect: {
      get () {
        return this.listOfProjectsAndTasks
          .filter((item) => this.reportFilters.Projects.includes(item.Project_ID))
          .map((project) => project.Project_ID)
      },
      set (val) {
        if (this.listOfProjectsAndTasks.length) {
          this.reportFilters.Projects = val
          this.putReportingFilterSettings()
        }
      }
    }
  },
  watch: {
    'settings.defaultTask': {
      handler (newVal) {
        if (newVal && newVal.Task_ID) {
          this.defaultTaskApplied = false
          this.$nextTick(() => {
            this.autoSelectDefaultTask()
          })
        }
      },
      deep: true
    }
  },
  methods: {
    autoSelectDefaultTask () {
      if (this.defaultTaskApplied) return
      if (!this.settings?.defaultTask?.Task_ID) return
      if (!this.listOfProjectsAndTasks.length) return

      const targetProjectId = String(this.settings.defaultTask.Project_ID)
      const targetTaskId = String(this.settings.defaultTask.Task_ID)

      if (!this.reportFilters.Projects.includes(targetProjectId)) {
        this.reportFilters.Projects = [...this.reportFilters.Projects, targetProjectId]
      }

      const fullProjectIndex = this.listOfProjectsAndTasks.findIndex(
        p => String(p.Project_ID) === targetProjectId
      )

      if (fullProjectIndex === -1) {
        console.log('project not found')
        return
      }

      const project = this.listOfProjectsAndTasks[fullProjectIndex]
      this.openedProjectId = project.Project_ID

      let foundTask = null
      const tasksToOpen = []

      const buildPath = (tasks = []) => {
        for (const task of tasks) {
          const currentId = String(task.Task_ID)

          if (currentId === targetTaskId) {
            foundTask = task
            return true
          }

          if (task.children && task.children.length) {
            if (buildPath(task.children)) {
              tasksToOpen.push(task)
              return true
            }
          }
        }

        return false
      }

      buildPath(project.Tasks || [])

      if (!foundTask) {
        console.log('task not found in tree')
        return
      }

      this.$set(this.openNodes, targetProjectId, [...tasksToOpen])

      this.panel = [fullProjectIndex]

      this.$set(this.activeNodes, targetProjectId, [foundTask])
      this.selectedTask([foundTask], targetProjectId)

      this.defaultTaskApplied = true
    },
    selectAll () {
      if (this.selectAllProjects) {
        this.listForSelect = this.listOfProjectsAndTasks.map((project) => project.Project_ID)
      }
    },
    datepickerChanges (e) {
      const changes = e ? e.split('-') : this.date.split('-')
      this.getReportingDays(changes[0], changes[1] || this.currentMonth)
    },
    notReportingDays (date) {
      if (+(new Date(date)) >= +(new Date(this.today))) {
        return false
      }
      const findReportingDay = this.reportingDays.find((rd) => +rd.Day_Of_Month === +date.split('-')[2])
      if (findReportingDay) {
        return [findReportingDay.Report_Dur >= 8 ? 'green lighten-1' : 'green darken-2']
      }
      return ['blue-grey lighten-4']
    },
    getReportingDays (year, month) {
      this.axios.get(`/api/get_reporting_days/${year}/${month}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then((res) => {
        this.reportingDays = res.data
        this.currentMonth = month
        this.currentYear = year
      })
    },
    handleSearch (input) {
      if (input) {
        this.panel = this.listOfProjectsAndTasks.map((_, i) => i)
        this.$nextTick(() => {
          if (this.$refs.tree) {
            this.$refs.tree.forEach((t) => t.updateAll(true))
          }
        })
      } else {
        this.panel = []
      }
    },
    async onTotalReportsUpdate () {
      await this.getProjectsAndTasks(false)
      this.getReportsByDateAndTask()
    },
    showDeleteTaskDialog (item) {
      this.deleteTaskDialog = true
      this.taskIdToDelete = item.Task_ID
    },
    closeDeleteTaskDialog () {
      this.deleteTaskDialog = false
    },
    deleteTask () {
      this.axios.delete(`/api/tasks/reps/list-of-projects-and-tasks/${this.taskIdToDelete}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.getProjectsAndTasks(false)
        this.closeDeleteTaskDialog()
      })
    },
    showCreateNewTaskDialog (item, isNew) {
      if (isNew) {
        this.newTask = {
          Name: null,
          Estimate: 40,
          Parent_Task_ID: item.Task_ID,
          Assigned_To: this.user.employee_id,
          root_task_id: item.root_task_id,
          Project_ID: item.Project_ID,
          Start_Date: new Date().toISOString().substring(0, 10),
          Is_Folder: false,
          Status: 'Open',
          Reported: 0,
          Show_IN_Report: true
        }
      } else {
        this.newTask = { ...item }
      }
      this.newTaskDialog = true
    },
    closeCreateNewTaskDialog () {
      this.newTaskDialog = false
    },
    createNewTask () {
      const method = this.newTask.Task_ID ? 'put' : 'post'
      const url = this.newTask.Task_ID ? `/api/tasks/${this.newTask.Task_ID}` : '/api/tasks'
      this.axios[method](url, this.newTask, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.getProjectsAndTasks(false)
        this.closeCreateNewTaskDialog()
      })
    },
    selectedTask (e, projectId) {
      if (e && e.length) {
        // Clear selection in all other projects
        for (const key of Object.keys(this.activeNodes)) {
          if (key !== String(projectId)) {
            this.$set(this.activeNodes, key, [])
          }
        }
        this.$set(this.activeNodes, projectId, [e[0]])
        if (!e[0].Is_Folder) {
          this.activeTask = e[0]
          this.getReportsByDateAndTask()
        }
      }
    },
    onFormChanged (e) {
      this.datepickerSaveValue = this.date
      this.formWasChanged = e
    },
    displayAlertDialog (e) {
      this.datepickerWantValue = e
      if (this.formWasChanged) {
        this.showAlertDialog = true
        this.date = this.datepickerSaveValue
      } else {
        this.changeDate()
      }
    },
    onCancel () {
      this.showAlertDialog = false
      this.date = this.datepickerSaveValue
    },
    changeDate () {
      this.date = this.datepickerWantValue
      this.showAlertDialog = false
      this.formWasChanged = false
      if (this.activeTask) {
        this.getReportsByDateAndTask()
      }
      this.getReportingDays(this.date.split('-')[0], this.date.split('-')[1])
    },
    getTotalReport () {
      this.axios.post('/api/reportingsummary', {
        firstDayOfLastMonth: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
        lastDayOfLastMonth: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
        firstDayOfLastWeek: moment().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
        lastDayOfLastWeek: moment().subtract(1, 'weeks').endOf('isoWeek').format('YYYY-MM-DD'),
        currentDate: this.today,
        firstWeekDay: moment(this.today).startOf('isoWeek').format('YYYY-MM-DD'),
        lastWeekDay: moment(this.today).endOf('isoWeek').format('YYYY-MM-DD'),
        firstMonthDay: moment(this.today).startOf('month').format('YYYY-MM-DD'),
        lastMonthDay: moment(this.today).endOf('month').format('YYYY-MM-DD')
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then((response) => {
        this.$store.dispatch('setTotalReports', { total: response.data[0] })
      })
    },
    async getProjectsAndTasks (closePanels) {
      await this.axios.get('/api/tasks/reps/list-of-projects-and-tasks', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then((response) => {
        this.listOfProjectsAndTasks = response.data || []
        this.getReportingFilterSettings()
        if (closePanels) {
          this.panel = []
        }
      })
    },
    getReportsByDateAndTask () {
      if (this.activeTask) {
        this.axios.get(`/api/reports-by-date-and-task/${this.date}/${this.activeTask.Task_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then((response) => {
          this.reports = response.data
          if (!this.reports.length) {
            this.reports.push({
              Reporting_ID: null,
              Start_Date: this.date,
              Start_Time: '00:00',
              Real_Dur: null,
              Report_Dur: null,
              Invoice_Dur: null,
              Description: null,
              Task_ID: this.activeTask.Task_ID,
              Total_Report_Dur: this.activeTask.Total_Report_Dur,
              Total_Paid_Dur: this.activeTask.Total_Paid_Dur,
              Estimate: this.activeTask.Estimate,
              Reported: this.activeTask.Reported,
              Project_ID: this.activeTask.Project_ID,
              Project_Description: this.activeTask.Project_Name,
              Task_Name: this.activeTask.Name
            })
          }
          this.showReportForm = true
          this.updateReportsKey++
        })
      }
    },
    getReportingFilterSettings () {
      this.axios.get(`/api/report-filter/${this.user.employee_id}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then((response) => {
        if (response.data.length) {
          const settings = JSON.parse(response.data[0].Settings)
          this.reportFilters.Projects = settings.ProjectIds
          this.reportFilters.DateRange = settings.Dates
        }
      })
    },
    putReportingFilterSettings () {
      const body = {
        Settings: JSON.stringify({
          ProjectIds: this.reportFilters.Projects,
          Dates: this.reportFilters.DateRange
        })
      }
      this.axios.put(`/api/report-filter/${this.user.employee_id}`, body, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      })
    },
    onClearDates () {
      this.reportFilters.DateRange = []
      this.updateDates()
    },
    updateDates () {
      const body = {
        Settings: JSON.stringify({
          ProjectIds: this.reportFilters.Projects,
          Dates: this.reportFilters.DateRange
        })
      }
      this.axios.put(`/api/reports-by-date/${this.user.employee_id}`, body, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.datesMenu = false
      })
    },
    filteredTasksMethod (items) {
      return items
    }
  }
}
</script>

<style lang="scss" scoped>
.reporting-form-wrapper {
  position: sticky;
  top: 60px;
}
@media screen and (max-width: 1265px) {
  .row-columns-wrap {
    flex-direction: column-reverse;
    .first-col,
    .second-col {
      max-width: 100%;
    }
  }
}
::v-deep .v-treeview-node--active {
  background-color: rgba(25, 118, 210, 0.1) !important;
  .v-treeview-node__label {
    font-weight: bold;
    color: #1976d2 !important;
  }
}
</style>
